import { isPlatformServer, Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import { RouteConfigLoadEnd, RouteConfigLoadStart, Router } from '@angular/router';
import { Subscription, SubscriptionLike } from 'rxjs';
import { ModalHistoryService } from './modal-history.service';

@Component({
  selector: 'pxw-modal-history',
  template: '',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalHistoryComponent implements OnInit, OnDestroy {
  @Input() name: string | undefined;
  @Input() title: string | undefined;
  @Output() popState = new EventEmitter();
  @Input() isBoundToRoute = false;

  private popStateSubscription$: SubscriptionLike | undefined;
  private routerSubscription$: Subscription;
  private active = false;
  private isLoadingModule = false;

  private platformId = inject(PLATFORM_ID);

  private location = inject(Location);
  private router = inject(Router);
  private modayHistoryService = inject(ModalHistoryService);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get locationState(): any {
    return this.location.getState() || {};
  }

  ngOnInit() {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    this.modayHistoryService.push(this);

    this.popStateSubscription$ = this.location.subscribe(popState => {
      const isOurEntryConsumed = !popState.state || popState.state?.name !== this.name;

      if (isOurEntryConsumed && this.modayHistoryService.isActiveInstance(this)) {
        this.handleBack();
      }
    });

    this.routerSubscription$ = this.router.events.subscribe(event => {
      if (event instanceof RouteConfigLoadStart) {
        this.isLoadingModule = true;
      } else if (event instanceof RouteConfigLoadEnd) {
        this.isLoadingModule = false;
      }
    });

    /* If `isActive` is set and we haven't made an entry to the browser history state yet,
     * push our new entry onto the stack. From here on, when the browser back button is pressed,
     * the page won't change, and we can handle that event above to fire our internal functions. */
    if (!window.history.state || this.locationState.name !== this.name) {
      this.active = true;

      const stateWithName = {
        ...this.locationState,
        name: this.name,
        title: this.title,
      };

      if (this.isBoundToRoute) {
        this.location.replaceState(this.router.routerState.snapshot.url, undefined, stateWithName);
      } else {
        this.location.go(this.router.routerState.snapshot.url, undefined, stateWithName);
      }
    }
  }

  ngOnDestroy() {
    this.cleanupHistory();
  }

  private handleBack() {
    if (!this.active) {
      return;
    }
    this.cleanupHistory();
    this.popState.emit();
  }

  private cleanupHistory() {
    if (!this.active) {
      return;
    }
    this.active = false;

    this.modayHistoryService.tryPop(this);

    this.popStateSubscription$?.unsubscribe();
    this.routerSubscription$?.unsubscribe();

    if (
      // NOTE: for isBoundToRoute=true scenarios, modal-form consumer should handle onClose-navigation manually
      !this.isBoundToRoute &&
      this.location.getState() &&
      this.locationState.name === this.name &&
      !this.isLoadingModule
    ) {
      this.location.back();
    }
  }
}
